/**
 * Zwei Regeln zu EINER Frage: Was tut MAESTRO, wenn zwei Quellen etwas Verschiedenes sagen?
 *
 *  1. **Die Gegenüberstellung** (R3.4, Konzept § 3.4): abweichende Anschriften stehen
 *     NEBENEINANDER, jede mit Quelle und Stand. Nichts wird zusammengeführt, nichts überschrieben,
 *     keine Quelle zur „richtigen" erklärt.
 *  2. **Der GLEIF/LEI-Kreuzcheck** (R4.4, § 3.3): ein LEI-Datensatz BESTÄTIGT oder WIDERSPRICHT —
 *     er behauptet nie etwas allein. Ein Widerspruch landet in genau der Gegenüberstellung aus (1).
 *
 * Beide gehören in dieselbe Datei, weil sie dieselbe Haltung ausdrücken und ineinandergreifen:
 * GLEIF ist eine weitere Stimme in derselben Gegenüberstellung, keine zweite Anzeigefläche.
 *
 * ── Warum das keine Auswahl ist (Abgrenzung zu R3.3) ─────────────────────────────────────────
 * `@maestro/places-anzeige` löst einen benachbarten, aber ANDEREN Fall: dort liefert EINE Quelle
 * mehrere Treffer, und ein Mensch wählt den richtigen aus — danach ist die Sache entschieden.
 * Hier sprechen VERSCHIEDENE Quellen über dieselbe Firma. Es gibt nichts auszuwählen: das
 * Handelsregister nennt den eingetragenen Sitz, Google die Anschrift des Ladens, der
 * Kundendatensatz das, was der Betreiber gepflegt hat. Alle drei können zugleich stimmen. Ein
 * Auswahl-Knopf würde eine Entscheidung erzwingen, die niemand treffen muss — und dabei genau die
 * Information vernichten, die hier wertvoll ist: dass die Angaben auseinandergehen.
 * Deshalb gibt es in diesem Paket bewusst KEINE Handlung und KEINEN Rückgabewert, den ein
 * „Übernehmen" schreiben könnte.
 *
 * ── Warum ein eigenes Paket und kein `if` in der Komponente ──────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (Befund aus R0.2, seither in R0.7/R0.9/R1.6/R4.5/R3.3
 * bestätigt). Sätze wie „übereinstimmende Angaben erzeugen KEINEN Widerspruchs-Hinweis" oder
 * „ohne LEI passiert nichts" sind in einer React-Komponente unprüfbar — genau dort verschwinden
 * sie leise wieder. Gleiche Bauform wie `@maestro/anreicherung` (R0.7), `@maestro/firmenprofil`
 * (R0.9), `@maestro/recherche-stand` (R1.6), `@maestro/google-attribution` (R4.5),
 * `@maestro/places-anzeige` (R3.3): reine Regel im Paket, Tests in
 * `apps/api/test/quellen-widerspruch.test.ts` (jene Suite läuft garantiert in der CI). Dieses
 * Paket hat deshalb bewusst KEIN `test`-Skript — `apps/api/test/ci-testabdeckung.test.ts` verlangt
 * für jedes Paket MIT `test`-Skript einen eigenen CI-Aufruf.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zeitzone: `zeitzone` ist Parameter. Ohne sie
 * formatiert `Intl` in der Zone der Laufzeit — im Browser also in der des Betrachters (Vorgabe aus
 * R0.7), im Test in der übergebenen Zone (deterministisch).
 */

import { standZeile, type RechercheQuelle } from "@maestro/recherche-stand";

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 1) R3.4 — die Gegenüberstellung
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Die Quellen, die heute eine Anschrift zu einer Firma liefern können. Eine Teilmenge von
 * `RECHERCHE_QUELLEN` (R1.6) — die Namen werden nicht zweimal geschrieben, sonst hieße dieselbe
 * Quelle je nach Fläche anders.
 *
 * NICHT dabei: „Impressum". Die Auslesung ist zum Zeitpunkt dieses Kriteriums noch nicht gemergt
 * (R3.1). Sobald sie es ist, kommt sie hier als weiterer Eintrag dazu — die Regel selbst ändert
 * sich dadurch nicht, sie zählt Quellen und vergleicht Zeichenketten.
 */
export type AdressQuelle = Extract<RechercheQuelle, "Kundendatensatz" | "Handelsregister" | "Google Places" | "GLEIF">;

/** Eine Anschrift, so wie EINE Quelle sie führt. */
export interface AdressEingabe {
  quelle: AdressQuelle;
  /** Der Wortlaut der Quelle. Leer/fehlend heißt: diese Quelle sagt nichts — kein Eintrag. */
  adresse?: string | null;
  /**
   * Wann diese Quelle das gesagt hat. Bei Recherche-Quellen `looked_up_at`, beim Kundendatensatz
   * die letzte Änderung am Datensatz. Fehlt der Wert, sagt die Herkunftszeile „ohne Datum" —
   * ausdrücklich KEIN Rückfall auf irgendein anderes Datum (R1.6).
   */
  standIso?: string | null;
}

export interface AdressAbgleichLage {
  eintraege: readonly AdressEingabe[];
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

export interface AdressGegenueberstellungEintrag {
  readonly quelle: AdressQuelle;
  /**
   * Der Wortlaut der Quelle, UNVERÄNDERT — nur `trim()`. Hier steht nie eine normalisierte,
   * „bereinigte" oder zusammengeführte Fassung: Was die Quelle sagt, ist die Information.
   */
  readonly adresse: string;
  /** „Handelsregister · Stand 12.06.2026" bzw. „… · ohne Datum" (R1.6). */
  readonly herkunft: string;
  /**
   * `true`, wenn diese Angabe mindestens einer anderen WIDERSPRICHT (nicht bloß gröber ist).
   * Eine Angabe kann in der Gegenüberstellung stehen, ohne selbst zu widersprechen — der Sitz
   * „München" widerspricht „Musterstraße 1, 80331 München" nicht, er sagt nur weniger.
   */
  readonly widersprichtAnderen: boolean;
}

export interface AdressGegenueberstellung {
  /** Der eine Satz über der Liste. */
  readonly satz: string;
  /** Alle Quellen, die etwas sagen — auch die, die nicht widersprechen. Das ist das Bild. */
  readonly eintraege: readonly AdressGegenueberstellungEintrag[];
  /** Was NICHT passiert ist. Steht in der Regel, weil es die eigentliche Zusage ist. */
  readonly hinweis: string;
  /**
   * Die Werte, die diese Fläche aus Google anzeigt — Eingabe für `googleAttribution()` (R4.5).
   * Sie kommen aus derselben Regel, die die Einträge baut, damit Anzeige und Attributionspflicht
   * nicht auseinanderlaufen können.
   */
  readonly googleWerte: readonly unknown[];
}

/** Was NICHT passiert ist — der Kern des Kriteriums, wörtlich auf dem Schirm. */
export const KEINE_ZUSAMMENFUEHRUNG =
  "Nichts davon wurde zusammengeführt oder überschrieben. Welche Anschrift gilt, entscheiden Sie — " +
  "das ist eine Entscheidung, kein Rechenschritt.";

/** Überschrift der Fläche (Konzept § 3.4, Block „Zu prüfen"). */
export const GEGENUEBERSTELLUNG_TITEL = "Anschrift: die Quellen sagen Verschiedenes";

/**
 * Die Gegenüberstellung — oder `null`, wenn es keine gibt.
 *
 * `null` heißt ausdrücklich: hier ist NICHTS zu zeigen. Drei Wege dorthin, alle drei gewollt:
 *  1. Weniger als zwei Quellen sagen etwas — ein „Vergleich" mit einer Angabe ist keiner.
 *  2. Alle Angaben vertragen sich (siehe `vertraegtSich`) — die Gegenprobe gegen Fehlalarm. Eine
 *     Warnung, die bei jedem zweiten Kunden angeht, wird nach zwei Wochen überlesen, und dann
 *     wirkt auch die echte nicht mehr.
 *  3. Genau eine Quelle bleibt nach dem Aussortieren leerer Angaben übrig.
 *
 * Und `null` heißt NIE: „wir haben uns für eine Fassung entschieden".
 */
export function adressAbgleich(lage: AdressAbgleichLage): AdressGegenueberstellung | null {
  const vorhanden = lage.eintraege
    .map((e) => ({ ...e, adresse: e.adresse?.trim() ?? "" }))
    .filter((e): e is AdressEingabe & { adresse: string } => e.adresse !== "");
  if (vorhanden.length < 2) return null;

  const formen = vorhanden.map((e) => vergleichsform(e.adresse));

  // Widerspruch je Eintrag: gibt es mindestens einen anderen, mit dem er sich NICHT verträgt?
  const widerspruch = vorhanden.map((_, i) => formen.some((andere, j) => i !== j && !vertraegtSich(formen[i]!, andere)));
  if (!widerspruch.some(Boolean)) return null;

  const eintraege = vorhanden.map<AdressGegenueberstellungEintrag>((e, i) => ({
    quelle: e.quelle,
    adresse: e.adresse,
    herkunft: standZeile(e.quelle, e.standIso ?? null, lage.zeitzone),
    widersprichtAnderen: widerspruch[i] ?? false,
  }));

  const quellenZahl = eintraege.length;
  return Object.freeze({
    satz:
      `Diese Firma hat je nach Quelle eine andere Anschrift — ${quellenZahl} Quellen, ` +
      `${zaehlbareFassungen(formen)} verschiedene Fassungen.`,
    eintraege,
    hinweis: KEINE_ZUSAMMENFUEHRUNG,
    googleWerte: eintraege.filter((e) => e.quelle === "Google Places").map((e) => e.adresse),
  });
}

/**
 * Wie viele UNTERSCHIEDLICHE Fassungen unter den Angaben stecken — für den Satz.
 *
 * Gezählt wird über die Vergleichsform, nicht über den Wortlaut: „Musterstr. 1" und
 * „Musterstraße 1" sind EINE Fassung in zwei Schreibweisen, und ein Satz, der daraus zwei machte,
 * wäre selbst schon eine kleine Unwahrheit.
 */
function zaehlbareFassungen(formen: readonly string[]): number {
  return new Set(formen).size;
}

/* ────────────────────────────── Vergleichen, nicht vereinheitlichen ────────────────────────── */

/**
 * Die Form, in der zwei Anschriften VERGLICHEN werden — sie wird nie angezeigt.
 *
 * Das ist die heikelste Stelle des ganzen Kriteriums, deshalb ausdrücklich: Diese Funktion
 * entscheidet ausschließlich die Frage „ist das ein Widerspruch oder dieselbe Angabe in anderer
 * Schreibweise?". Sie erzeugt keinen Wert, der irgendwo gespeichert, übernommen oder angezeigt
 * würde. Wer sie zum Vereinheitlichen benutzte, hätte genau die stille Zusammenführung gebaut,
 * die § 3.4 verbietet.
 *
 * Was eingeebnet wird — jedes davon ist eine Schreibweise, keine andere Aussage:
 *  - Groß-/Kleinschreibung, „ß" gegen „ss", Umlaut gegen Umschrift („München"/„Muenchen");
 *  - „str." / „straße" / „strasse" als eigenes Wort (die häufigste Abkürzung im Land), egal ob
 *    angehängt („Musterstraße") oder getrennt („Muster Str.");
 *  - Satzzeichen, Bindestriche und Mehrfach-Leerzeichen;
 *  - das angehängte Land („Deutschland"/„Germany"): Google hängt es an, kein Mensch tippt es.
 *
 * Was ausdrücklich NICHT eingeebnet wird: Hausnummern, Postleitzahlen, Ortsnamen, Zusätze wie
 * „c/o" oder „Hinterhaus". Das sind Aussagen. Wer sie wegnormalisierte, würde Widersprüche
 * verstecken — und Widersprüche zu verstecken ist genau der Fehler, gegen den dieses Kriterium
 * gebaut ist.
 */
export function vergleichsform(adresse: string): string {
  return adresse
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/[.,;/()-]/g, " ")
    // Erst das angehängte „…straße"/„…str" abtrennen, dann die freistehende Fassung angleichen —
    // in dieser Reihenfolge landen „Musterstraße 1", „Musterstr. 1" und „Muster Str. 1" alle drei
    // auf „muster str 1". Umgekehrt bliebe „musterstr" ein Wort und „muster str" zwei.
    .replace(/([a-z]{2,})(strasse|str)\b/g, "$1 $2")
    .replace(/\bstrasse\b/g, "str")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+(deutschland|germany)$/, "")
    .trim();
}

/**
 * Vertragen sich zwei Angaben?
 *
 * Ja, wenn sie gleich sind ODER wenn die eine vollständig in der anderen steckt (jedes Wort der
 * kürzeren kommt in der längeren vor). Der Grund steht in den Daten: `handelsregister_lookups`
 * führt den **Sitz** — oft nur „München" —, `google_places_lookups` die **Anschrift des Ladens**
 * („Musterstraße 1, 80331 München"). Beide sind wahr. Wer daraus einen Widerspruch machte, würde
 * bei fast jedem Kunden mit Registereintrag eine Warnung erzeugen, und die eine echte ginge darin
 * unter.
 *
 * Nein, sobald ein Wort der einen in der anderen fehlt: „Berlin" gegen „… 80331 München" ist ein
 * Widerspruch, und genau der soll dastehen.
 */
export function vertraegtSich(a: string, b: string): boolean {
  if (a === b) return true;
  const [kurz, lang] = a.split(" ").length <= b.split(" ").length ? [a, b] : [b, a];
  const langeWorte = new Set(lang.split(" ").filter(Boolean));
  const kurzeWorte = kurz.split(" ").filter(Boolean);
  if (kurzeWorte.length === 0) return true;
  return kurzeWorte.every((w) => langeWorte.has(w));
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 2) R4.4 — GLEIF/LEI als Kreuzcheck, nie als Primärquelle
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Der Teil eines GLEIF-Datensatzes, den MAESTRO überhaupt ansieht (GLEIF-API v1, `lei-records`).
 * GLEIF-Daten sind Open Data unter CC0 (https://www.gleif.org/en/about/open-data) — es gibt weder
 * eine Attributionspflicht wie bei Google noch ein Speicherverbot. Die Grenze hier ist keine
 * lizenzrechtliche, sondern eine fachliche: mehr als Name, Anschrift und Status braucht ein
 * Kreuzcheck nicht.
 */
export interface GleifDatensatz {
  /** `entity.legalName.name` — der amtlich registrierte Name. */
  legalName?: string | null;
  /** `entity.legalAddress`, zu einer Zeile zusammengesetzt (siehe `apps/api/src/lib/gleif.ts`). */
  legalAddress?: string | null;
  /** `entity.status` — ACTIVE | INACTIVE | NULL. */
  entityStatus?: string | null;
  /** `registration.status` — ISSUED | LAPSED | RETIRED | … (Zustand des LEI, nicht der Firma). */
  registrationStatus?: string | null;
}

export interface GleifKreuzcheckLage {
  /** Der LEI, gegen den geprüft wurde. Fehlt er, passiert NICHTS — das ist der Kernfall. */
  lei?: string | null;
  /** Was GLEIF geantwortet hat. `null` = nicht (mehr) vorhanden, nicht gefunden, Fehler. */
  datensatz?: GleifDatensatz | null;
  /** Wann GLEIF gefragt wurde — der Stand dieser Angabe (R1.6). */
  geprueftAmIso?: string | null;
  /**
   * Was MAESTRO ohne GLEIF schon weiß. **Das ist die Sperre gegen „Primärquelle":** Der Kreuzcheck
   * vergleicht ausschließlich gegen einen vorhandenen eigenen Wert. Gibt es keinen, hat GLEIF
   * nichts zu bestätigen und nichts zu widerlegen — und behauptet dann auch nichts.
   */
  bekannt: { firma?: string | null; adresse?: string | null };
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

export interface GleifBefund {
  readonly feld: "Name" | "Anschrift";
  /** Was MAESTRO bisher führt — unverändert. */
  readonly eigenerWert: string;
  /** Was GLEIF führt — unverändert. */
  readonly gleifWert: string;
  /** `true` = bestätigt, `false` = weicht ab. Mehr Zustände hat ein Kreuzcheck nicht. */
  readonly stimmtUeberein: boolean;
}

export interface GleifKreuzcheckAnzeige {
  readonly lei: string;
  /** „GLEIF · Stand 06.08.2026" bzw. „… · ohne Datum" (R1.6). */
  readonly herkunft: string;
  readonly satz: string;
  readonly befunde: readonly GleifBefund[];
  /** Was dieser Block ausdrücklich NICHT ist. */
  readonly hinweis: string;
}

/** Die Zusage, die diesen Block von einer Quelle unterscheidet. */
export const GLEIF_NUR_KREUZCHECK =
  "GLEIF bestätigt oder widerspricht — die Angaben werden davon weder ersetzt noch überschrieben.";

/**
 * Der Kreuzcheck — oder `null`.
 *
 * `null` in JEDEM dieser Fälle, und in keinem davon passiert sonst irgendetwas (kein Fehler, keine
 * leere Karte, kein Ladezustand):
 *  1. **Kein LEI.** Der Normalfall in Deutschland: einen LEI haben fast nur Finanzmarkt-Teilnehmer
 *     und größere Kapitalgesellschaften (§ 3.3). Für den typischen Catering-Kunden ist hier nichts
 *     zu tun — und „nichts zu tun" sieht auch nach nichts aus.
 *  2. **Kein Datensatz.** Nicht gefunden, Abruf fehlgeschlagen, noch nicht gefragt: GLEIF ist eine
 *     Gegenprobe. Dass eine Gegenprobe fehlt, ist keine Aussage über die Firma und gehört nicht
 *     als Fehlermeldung an einen Entscheidungsort.
 *  3. **Nichts zu vergleichen.** Weder ein eigener Firmenname noch eine eigene Anschrift liegt vor
 *     — oder GLEIF liefert zu beidem nichts. Dann bliebe nur, GLEIFs Angaben SELBST hinzuschreiben,
 *     und genau das ist „Primärquelle". Diese Zeile ist die technische Fassung der Auflage aus
 *     § 3.3: **nie als Primärquelle.**
 */
export function gleifKreuzcheck(lage: GleifKreuzcheckLage): GleifKreuzcheckAnzeige | null {
  const lei = lage.lei?.trim();
  if (!lei) return null;
  const datensatz = lage.datensatz;
  if (!datensatz) return null;

  const befunde: GleifBefund[] = [];
  const namePaar = paar(lage.bekannt.firma, datensatz.legalName);
  if (namePaar) {
    befunde.push({
      feld: "Name",
      eigenerWert: namePaar.eigen,
      gleifWert: namePaar.fremd,
      // Namen werden nachsichtiger verglichen als Anschriften: „GmbH" gegen „GmbH" mit anderem
      // Abstand oder Groß-/Kleinschreibung ist keine Abweichung. Rechtsformzusätze bleiben drin —
      // eine „Muster GmbH" ist nicht dieselbe Rechtsform wie eine „Muster AG".
      stimmtUeberein: vergleichsform(namePaar.eigen) === vergleichsform(namePaar.fremd),
    });
  }
  const adressPaar = paar(lage.bekannt.adresse, datensatz.legalAddress);
  if (adressPaar) {
    befunde.push({
      feld: "Anschrift",
      eigenerWert: adressPaar.eigen,
      gleifWert: adressPaar.fremd,
      // DIESELBE Regel wie in der Gegenüberstellung (R3.4) — sonst gälte ein Unterschied hier als
      // Abweichung und dort als Schreibweise, und ein Betreiber sähe zwei Antworten auf dieselbe
      // Frage.
      stimmtUeberein: vertraegtSich(vergleichsform(adressPaar.eigen), vergleichsform(adressPaar.fremd)),
    });
  }
  if (befunde.length === 0) return null;

  const abweichend = befunde.filter((b) => !b.stimmtUeberein);
  return Object.freeze({
    lei,
    herkunft: standZeile("GLEIF", lage.geprueftAmIso ?? null, lage.zeitzone),
    satz:
      abweichend.length === 0
        ? `Der LEI-Datensatz bestätigt ${befunde.length === 1 ? "die Angabe" : "die Angaben"}.`
        : `Der LEI-Datensatz weicht ab: ${abweichend.map((b) => b.feld).join(" und ")}.`,
    befunde,
    hinweis: GLEIF_NUR_KREUZCHECK,
  });
}

/** Ein Vergleichspaar — nur wenn BEIDE Seiten etwas sagen. Einseitig ist kein Kreuzcheck. */
function paar(eigen: string | null | undefined, fremd: string | null | undefined): { eigen: string; fremd: string } | null {
  const a = eigen?.trim();
  const b = fremd?.trim();
  return a && b ? { eigen: a, fremd: b } : null;
}

/**
 * Die GLEIF-Anschrift als Stimme in der Gegenüberstellung aus R3.4 — oder `null`.
 *
 * Hier greifen die beiden Kriterien ineinander: Ein Widerspruch, den der Kreuzcheck feststellt,
 * soll nicht in einem eigenen Kasten stehen, sondern an derselben Stelle wie jeder andere
 * Quellen-Widerspruch. Und weil `adressAbgleich()` mindestens ZWEI Angaben braucht, kann die
 * GLEIF-Anschrift dort nie allein erscheinen — auch auf diesem Weg wird GLEIF nicht zur
 * Primärquelle. Das ist keine Absprache, sondern eine Eigenschaft der Regel (und geprüft).
 */
export function gleifAlsAdressQuelle(lage: GleifKreuzcheckLage): AdressEingabe | null {
  const lei = lage.lei?.trim();
  const adresse = lage.datensatz?.legalAddress?.trim();
  if (!lei || !adresse) return null;
  return { quelle: "GLEIF", adresse, standIso: lage.geprueftAmIso ?? null };
}
